
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, UserCog } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { KolPic } from "@/types/table";

interface ModifyAccessDialogProps {
  user: KolPic;
  onAccessChanged: () => Promise<void>;
}

export function ModifyAccessDialog({ user, onAccessChanged }: ModifyAccessDialogProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState(user.access || "limited");
  const { toast } = useToast();

  const handleUpdateAccess = async () => {
    try {
      setIsUpdating(true);
      
      // Ensure we have proper authentication context
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (!authUser) {
        toast({
          title: "Authentication Error",  
          description: "You must be logged in to perform this action",
          variant: "destructive",
        });
        return;
      }
      
      console.log('Updating access for user:', user.id, 'to access:', selectedAccess);
      console.log('Current auth user:', authUser.id);
      
      const { error } = await supabase
        .from('kol_pic')
        .update({ 
          access: selectedAccess,
          updated_at: new Date().toISOString()
        })
        .eq('auth_id', user.auth_id);
      
      if (error) {
        console.error('Update error details:', error);
        throw error;
      }
      
      toast({
        title: "Success",
        description: `Access level updated for ${user.name}`,
      });
      
      setDialogOpen(false);
      await onAccessChanged();
      
    } catch (err: any) {
      console.error("Failed to update access level:", err);
      toast({
        title: "Update Failed",  
        description: err.message || "Failed to update access level. Please check your permissions.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 w-20 text-xs">
          <UserCog className="h-3 w-3 mr-1" />
          Access
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Modify Access Level</DialogTitle>
          <DialogDescription>
            Update access level for {user.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Access Level</label>
            <Select value={selectedAccess} onValueChange={setSelectedAccess}>
              <SelectTrigger>
                <SelectValue placeholder="Select access level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="limited">Limited Access</SelectItem>
                <SelectItem value="bu-access">BU Access</SelectItem>
                <SelectItem value="full-access">Full Access</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleUpdateAccess} disabled={isUpdating}>
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Update"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
